import React, { useMemo } from "react";
import ModulePreviewRenderer from "./ModulePreviewRenderer";
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDraggable } from '@dnd-kit/core';
import moduleElementsService from '../utils/moduleElementsService';

const DraggableTemplateItem = ({ template, isCanvasEmpty }) => {
  const { t } = useTranslation();

  const isCorridor = template.config?.subtype === 'corridor';
  const isDisabled = isCanvasEmpty && isCorridor;

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `draggable-template-${template.id}`,
    data: { template },
    disabled: isDisabled,
  });

  const generateTooltipText = (tpl) => {
    if (isDisabled) {
      return "Коридорні модулі не можна розміщувати на порожньому полі.";
    }

    const config = typeof tpl.config === 'string' ? JSON.parse(tpl.config) : tpl.config;
    const subtype = config?.subtype;

    const completeDescription = moduleElementsService.getCompleteElementsDescription(subtype);
    const allElements = [...(config.predefinedElements || []), ...(config.freeElements || [])];
    let manualElements = '';

    if (allElements.length > 0) {
      const elementCounts = allElements.reduce((acc, el) => {
        const elementName = t(`prices.elements.${el.type.toLowerCase()}`, el.type);
        acc[elementName] = (acc[elementName] || 0) + 1;
        return acc;
      }, {});
      const elementStrings = Object.entries(elementCounts).map(([name, count]) => `${name} x ${count}`);
      manualElements = elementStrings.join(', ');
    }

    let tooltip = '';
    if (completeDescription) {
      tooltip = `Модуль з елементами:\n${completeDescription}`;
    } else if (manualElements) {
      tooltip = `Модуль з розміщеними елементами:\n${manualElements}`;
    } else {
      tooltip = "Порожній модуль, без додаткових елементів.";
    }

    return tooltip;
  };

  const tooltipText = generateTooltipText(template);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title={tooltipText}
      className={`p-3 rounded-lg shadow-sm transform transition-all duration-150 border border-gray-200 ${isDisabled
        ? 'bg-gray-200 opacity-60 cursor-not-allowed'
        : 'bg-gray-50 hover:bg-white hover:shadow-md hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 focus:ring-offset-card-bg-light cursor-grab active:cursor-grabbing'
        }`}
    >
      <div className="flex flex-col items-center text-center space-y-2 pointer-events-none">
        <div className="flex-shrink-0 w-full aspect-square bg-white rounded-md flex items-center justify-center overflow-hidden border border-gray-300 shadow-inner">
          <ModulePreviewRenderer template={template} />
        </div>
        <div className="text-center flex-1">
          <p className="text-xs font-medium text-gray-800 leading-tight text-center break-words">
            {template.name}
            {template.config && (typeof template.config === 'string' ? JSON.parse(template.config).sizeText : template.config.sizeText) && (
              <>
                <br />
                {(typeof template.config === 'string' ? JSON.parse(template.config).sizeText : template.config.sizeText)}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

const ModuleTemplateSelectionPanel = ({ templates, onClose, isCanvasEmpty }) => {
  const { t } = useTranslation();

  const groupedAndSortedTemplates = useMemo(() => {
    const grouped = templates.reduce((acc, template) => {
      const category = template.category || t('moduleTemplatePanel.other');
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    }, {});

    const PREFERRED_ORDER = ["Базові", "Житлові", "Санітарні", "Коридори", "Сходи", "Спеціального призначення"];

    return Object.entries(grouped).sort(([catA], [catB]) => {
      const indexA = PREFERRED_ORDER.indexOf(catA);
      const indexB = PREFERRED_ORDER.indexOf(catB);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return catA.localeCompare(catB);
    });
  }, [templates, t]);

  const showCloseButton = !!onClose;

  return (
    <div
      id="module-template-selection-panel"
      className="w-[480px] bg-card-bg-light border-r border-gray-300 p-4 overflow-y-auto flex-shrink-0 text-gray-700 h-full"
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">{t('moduleTemplatePanel.title')}</h2>
        {showCloseButton && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title={t('moduleTemplatePanel.close')}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        )}
      </div>

      {groupedAndSortedTemplates.map(([categoryName, categoryTemplates]) => (
        <div key={categoryName} className="mb-6">
          <h3 className="text-sm font-semibold text-primary-blue mb-3 border-b border-gray-300 pb-2">
            {categoryName}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {categoryTemplates.map((template) => (
              <DraggableTemplateItem key={template.id} template={template} isCanvasEmpty={isCanvasEmpty} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ModuleTemplateSelectionPanel;